import cn from 'classnames'
import { PlausibleEvent } from 'enums/plausible'
import { FC, useCallback, useState } from 'react'
import languages from 'static/languages'
import usePlausible from 'utils/hooks/usePlausible'

import { useTranslation } from 'next-i18next'

import Section from 'components/Section/Section'

import styles from './Support.module.scss'

interface ICard {
  year: number
  withdrawalSum: string
  withdrawalProcessingSpeed: number
  withdrawalProcessingSpeedUnit: string
  tradeProcessingSpeed: number
  staff: number
  flags: Array<string>
  active?: boolean
  onClick?: () => void
}

const Card: FC<ICard> = ({
  active,
  year,
  flags,
  withdrawalProcessingSpeed,
  withdrawalProcessingSpeedUnit,
  withdrawalSum,
  tradeProcessingSpeed,
  staff,
  onClick,
}) => {
  const { t } = useTranslation(['stats'])
  const flagsList = languages.filter((lang) => {
    return flags.includes(lang.locale)
  })

  // t(`stats:ourProgressMin`)
  // t(`stats:ourProgressHour`)
  // t(`stats:ourProgressHours`)
  // t(`stats:ourProgressDay`)
  // t(`stats:ourProgressDays`)
  return (
    <div
      id={`about-stats-support_${year}_card-div`}
      onClick={onClick}
      className={cn(styles.supportCardWrapper, { [styles.active]: active })}
    >
      <div className={styles.supportCard}>
        <div className={styles.cardTitle}>
          <div>${withdrawalSum}</div>
          <span>{t('stats:supportMoneyWithdrawn')}</span>
        </div>
        <div className={styles.flags}>
          <div className={styles.flagsTitle}>{t('stats:supportSupportLangs')}</div>
          <div className={styles.flagList}>
            {flagsList.map((flag, idx) => {
              return <img key={idx} src={flag.iconSrc} alt={flag.country} />
            })}
          </div>
        </div>
        <div className={styles.char}>
          <div className={styles.title}>{withdrawalProcessingSpeed}</div>
          <div className={styles.subtitle}>
            {t(`stats:ourProgress${withdrawalProcessingSpeedUnit}`)}
          </div>
          <div className={styles.description}>{t('stats:supportWithdrawalTimeline')}</div>
        </div>
        <div className={styles.char}>
          <div className={styles.title}>{staff}</div>
          <div className={styles.subtitle}>{t('stats:supportPeople')}</div>
          <div className={styles.description}>{t('stats:supportStaffSize')}</div>
        </div>
        <div className={styles.char}>
          <div className={styles.title}>{tradeProcessingSpeed}</div>
          <div className={styles.subtitle}>{t('stats:supportSeconds')}</div>
          <div className={styles.description}>{t('stats:supportTransactionTime')}</div>
        </div>
      </div>
      <div className={styles.Year}>
        <span className={styles.YearTitle}>{year}</span>
        <span className={styles.YearDesc}>{t('stats:supportYear')}</span>
      </div>
    </div>
  )
}

const cards: ICard[] = [
  {
    year: 2022,
    withdrawalProcessingSpeed: 30,
    withdrawalProcessingSpeedUnit: 'Min',
    withdrawalSum: '3 532 153',
    staff: 50,
    tradeProcessingSpeed: 0.7,
    flags: ['en', 'bn', 'hi', 'vi', 'es', 'th', 'tr', 'pt'],
  },
  {
    year: 2021,
    withdrawalProcessingSpeed: 1,
    withdrawalProcessingSpeedUnit: 'Hour',
    withdrawalSum: '1 971 849',
    staff: 40,
    tradeProcessingSpeed: 2,
    flags: ['en', 'bn', 'hi', 'vi', 'es', 'th', 'tr', 'pt'],
  },
  {
    year: 2020,
    withdrawalProcessingSpeed: 2,
    withdrawalProcessingSpeedUnit: 'Hours',
    withdrawalSum: '893 842',
    staff: 30,
    tradeProcessingSpeed: 4,
    flags: ['en', 'bn', 'hi', 'vi', 'es', 'th', 'tr', 'pt'],
  },
  {
    year: 2019,
    withdrawalProcessingSpeed: 1,
    withdrawalProcessingSpeedUnit: 'Day',
    withdrawalSum: '234 112',
    staff: 15,
    tradeProcessingSpeed: 8,
    flags: ['en', 'bn', 'hi', 'vi', 'es'],
  },
  {
    year: 2018,
    withdrawalProcessingSpeed: 3,
    withdrawalProcessingSpeedUnit: 'Days',
    withdrawalSum: '10 302',
    staff: 2,
    tradeProcessingSpeed: 10,
    flags: ['en', 'bn', 'hi'],
  },
]

const Support = ({ referrer }) => {
  const { t } = useTranslation(['stats'])
  const [active, setActive] = useState(2)
  const setEvent = usePlausible()
  const onClick = useCallback(
    (card: ICard, idx: number) => {
      setActive(idx)
      setEvent(PlausibleEvent.ClickedStatsCards, {
        statsYear: card.year,
        referrer,
      })
    },
    [setEvent],
  )
  return (
    <Section backgroundClass={styles.background}>
      <div className={styles.Grid}>
        <div className={styles.Header}>{t('stats:supportTitle')}</div>
        <div className={styles.CardsList}>
          {cards.map((card, idx) => {
            return (
              <Card
                onClick={() => onClick(card, idx)}
                active={active === idx}
                key={idx}
                {...card}
              />
            )
          })}
        </div>
        <div className={styles.years_wrapper}>
          {cards.map((card, idx) => {
            return (
              <button
                onClick={() => onClick(card, idx)}
                key={idx}
                id={`about-stats-support_${idx}_year-button`}
                className={cn(styles.year_container, { [styles.active]: active === idx })}
              >
                <span className={styles.Title}>{card.year}</span>&nbsp;
                <span className={styles.Desc}>{t('stats:supportYear')}</span>
              </button>
            )
          })}
        </div>
      </div>
    </Section>
  )
}

export default Support
